import React, { useCallback } from 'react';
import Hotel from '@this/src/domain/hotel/hotel';
import type { HotelJson, SlideSuggestion } from '@this/src/domain/select_repository';
import HotelCandidateBox from '@this/src/components/reserve_trip/select/hotel_box/hotel_candidate_box';
import type HotelList from '@this/src/domain/hotel/hotel_list';
import type User from '@this/src/domain/user/user';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import { digits } from '@this/src/util/price/price.util';
import { Loading } from '@this/shared/ui/feedbacks/loading';

interface Props {
  slideSuggestion: SlideSuggestion;
  slideHotels: HotelJson[];
  slideLoading: boolean;
  searchQueryId: number | null;
  includeTax: boolean;
  hotels: HotelList;
  destLocation: { lat: number; lng: number } | null;
  me: User | null;
  user: any;
  hotelPriceLimit: number | null;
  queryItem: SearchQueryItem;
  intlSpCandidatePrice?: boolean;
}

const SlideSuggestionHotel: React.FC<Props> = ({
  slideSuggestion,
  slideHotels,
  slideLoading,
  searchQueryId,
  includeTax,
  hotels,
  destLocation,
  me,
  user,
  hotelPriceLimit,
  queryItem,
  intlSpCandidatePrice = false
}) => {
  const handleSelectSlideHotel = useCallback(
    (hotel: Hotel) => {
      // 近隣駅のホテルをリストに追加して選択状態にする
      // 元の検索結果は保持したまま、このホテルだけを追加して選択
      if (hotels && hotel.id) {
        // リストに既に存在する場合は更新、存在しない場合は追加
        const existingHotel = hotels.find(hotel.id);
        if (existingHotel) {
          hotels.updateHotelList(hotel);
        } else {
          // リストの最後に追加
          hotels.list.push(hotel);
          hotels.filteredList.push(hotel);
        }
        hotels.selectWithAnimation(hotel.id);
      }
    },
    [hotels]
  );

  if (!slideSuggestion) {
    return null;
  }

  const topHotel =
    slideHotels && slideHotels.length > 0
      ? new Hotel({
          ...slideHotels[0],
          showFee: false,
          includeTax,
          marginType: undefined
        })
      : null;

  const selectedHotel = hotels?.list.find(hotel => hotel.id === hotels.currentId);
  const selectedPrice = selectedHotel?.getAveragePrice();
  const topHotelPrice = topHotel?.getAveragePrice();
  const priceDiff = selectedPrice && topHotelPrice ? selectedPrice - topHotelPrice : 0;

  // 検索中（slideLoadingがtrue）の場合、選択ホテルが存在し価格が既知の場合は非表示
  if (slideLoading && selectedHotel && selectedPrice !== undefined && selectedPrice !== null) {
    return null;
  }

  // 検索完了後、topHotelが存在し、選択ホテルより安い場合のみ表示
  if (!slideLoading && topHotel && priceDiff > 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '10px',
          padding: '12px',
          background: 'linear-gradient(135deg, #fff5e6 0%, #ffe6cc 100%)',
          border: '2px solid #ff9800',
          borderRadius: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <span style={{ fontSize: '18px' }}>💡</span>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#e65100' }}>近隣駅のおすすめホテル</div>
        </div>
        {slideSuggestion.transit && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px' }}>
            <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#333' }}>
              {slideSuggestion.transit.to}駅
            </span>
            <span style={{ fontSize: '12px', color: '#666' }}>
              （{slideSuggestion.transit.from}駅から約{slideSuggestion.transit.minutes}分）
            </span>
          </div>
        )}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '13px', color: '#e65100', fontWeight: 500 }}>
            約<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#d84315' }}>{digits(priceDiff)}円</span>
            お得なホテルがあります
          </div>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '6px', overflow: 'hidden' }}>
          <HotelCandidateBox
            destLocation={destLocation}
            me={me}
            user={user}
            hotel={topHotel}
            inList
            hotels={hotels}
            selected={false}
            serviceId={undefined}
            hotelPriceLimit={hotelPriceLimit}
            searchQueryId={searchQueryId}
            handleSelect={handleSelectSlideHotel}
            intlSpCandidatePrice={intlSpCandidatePrice}
          />
        </div>
      </div>
    );
  }

  // 検索中で選択ホテルが存在しない場合（初回検索時など）はローディングを表示
  if (slideLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '10px',
          padding: '12px',
          background: 'linear-gradient(135deg, #fff5e6 0%, #ffe6cc 100%)',
          border: '2px solid #ff9800',
          borderRadius: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <span style={{ fontSize: '18px' }}>💡</span>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#e65100' }}>近隣駅のおすすめホテル</div>
        </div>
        {slideSuggestion.transit && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px' }}>
            <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#333' }}>
              {slideSuggestion.transit.to}駅
            </span>
            <span style={{ fontSize: '12px', color: '#666' }}>
              （{slideSuggestion.transit.from}駅から約{slideSuggestion.transit.minutes}分）
            </span>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '6px'
          }}
        >
          <Loading size="small" />
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
            近隣駅のおすすめホテルを調べています...
          </div>
        </div>
      </div>
    );
  }

  // それ以外の場合は非表示
  return null;
};

export default SlideSuggestionHotel;
